import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as api from '../services/projectItemsService';
import type {
  ProjectItemRecord,
  CreateItemPayload,
  UpdateItemPayload,
} from '../types/viewerProjectItems';

export interface UseProjectItemsReturn {
  tools: ProjectItemRecord[];
  parts: ProjectItemRecord[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
  createItem: (payload: CreateItemPayload) => Promise<ProjectItemRecord | null>;
  updateItem: (itemId: string, fields: UpdateItemPayload) => Promise<ProjectItemRecord | null>;
  deleteItem: (itemId: string) => Promise<boolean>;
  reorderItems: (orderedIds: string[]) => Promise<boolean>;
}

/**
 * Hook that fetches and manages project items (tools & parts) from the Items API.
 *
 * Features:
 * - AbortController signal forwarded to API client (cancel on unmount/re-fetch)
 * - Optimistic updates with snapshot-based rollback on failure
 * - Mutation queue to prevent interleaving
 * - Re-fetch when companyId or productId changes (50ms debounce)
 * - Retains stale data on error, sets error string
 * - Skips fetch if companyId or productId is empty string
 */
export function useProjectItems(companyId: string, productId: string): UseProjectItemsReturn {
  const [items, setItems] = useState<ProjectItemRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for AbortController, debounce timer, and mutation queue
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mutationQueueRef = useRef<Promise<void>>(Promise.resolve());
  const mountedRef = useRef(true);
  const itemsRef = useRef<ProjectItemRecord[]>(items);

  // Keep itemsRef in sync with state for snapshot access in queued mutations
  itemsRef.current = items;

  // Split items into tools and parts
  const tools = useMemo(
    () => items.filter((item) => item.itemType === 'tool'),
    [items],
  );
  const parts = useMemo(
    () => items.filter((item) => item.itemType === 'part'),
    [items],
  );

  /**
   * Core fetch function. Aborts any in-flight request before starting a new one.
   */
  const fetchItems = useCallback(() => {
    // Skip fetch if companyId or productId is empty
    if (!companyId || !productId) {
      setItems([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Abort previous fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);

    api
      .listItems(companyId, productId, undefined, controller.signal)
      .then((data) => {
        if (!mountedRef.current) return;
        setItems(data);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!mountedRef.current) return;
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') return;
        // Retain stale data on error, just set error string
        const message = err instanceof Error ? err.message : 'Failed to fetch items';
        setError(message);
      })
      .finally(() => {
        if (!mountedRef.current) return;
        if (abortControllerRef.current === controller) {
          setIsLoading(false);
        }
      });
  }, [companyId, productId]);

  /**
   * Public refetch — can be called by consumers to force a refresh.
   */
  const refetch = useCallback(() => {
    fetchItems();
  }, [fetchItems]);

  // Debounced fetch on companyId/productId change
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchItems();
    }, 50);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [fetchItems]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Enqueue a mutation to prevent interleaving.
   * Each mutation: snapshot → optimistic update → API call → rollback on failure → refetch on success.
   */
  function enqueueMutation<T>(
    optimisticUpdate: (currentItems: ProjectItemRecord[]) => ProjectItemRecord[],
    apiCall: () => Promise<T>,
  ): Promise<T | null> {
    const promise = mutationQueueRef.current.then(async () => {
      // Snapshot current state for rollback (use ref for latest state in queued context)
      const snapshot = itemsRef.current;

      // Apply optimistic update
      const optimisticItems = optimisticUpdate(snapshot);
      setItems(optimisticItems);

      try {
        const result = await apiCall();
        // On success, refetch to confirm server state
        if (mountedRef.current) {
          fetchItems();
        }
        return result;
      } catch (err: unknown) {
        // Rollback to snapshot on failure
        if (mountedRef.current) {
          setItems(snapshot);
          const message = err instanceof Error ? err.message : 'Mutation failed';
          setError(message);
        }
        return null;
      }
    });

    // Update the queue reference (void version for chaining)
    mutationQueueRef.current = promise.then(() => undefined);

    return promise;
  }

  /**
   * Create a new item. Optimistically adds to the correct array.
   */
  const createItem = useCallback(
    (payload: CreateItemPayload): Promise<ProjectItemRecord | null> => {
      // Create a temporary optimistic record
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const now = new Date().toISOString();
      const currentItems = itemsRef.current;
      const optimisticRecord: ProjectItemRecord = {
        id: tempId,
        workspaceId: '',
        companyId,
        productId,
        itemType: payload.itemType,
        name: payload.name,
        code: payload.code ?? null,
        icon: payload.icon ?? null,
        description: payload.description ?? null,
        category: payload.category ?? null,
        imageUrl: payload.imageUrl ?? null,
        available: payload.available ?? true,
        sortOrder: payload.sortOrder ?? currentItems.length,
        metadata: payload.metadata ?? {},
        createdByUserId: null,
        createdAt: now,
        updatedAt: now,
      };

      return enqueueMutation(
        (current) => [...current, optimisticRecord],
        () => api.createItem(companyId, productId, payload),
      );
    },
    [companyId, productId, fetchItems],
  );

  /**
   * Update an existing item. Optimistically updates the item in the correct array.
   */
  const updateItem = useCallback(
    (itemId: string, fields: UpdateItemPayload): Promise<ProjectItemRecord | null> => {
      return enqueueMutation(
        (current) =>
          current.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  ...Object.fromEntries(
                    Object.entries(fields).filter(([, v]) => v !== undefined),
                  ),
                  updatedAt: new Date().toISOString(),
                }
              : item,
          ),
        () => api.updateItem(companyId, productId, itemId, fields),
      );
    },
    [companyId, productId, fetchItems],
  );

  /**
   * Delete an item. Optimistically removes from the correct array.
   */
  const deleteItem = useCallback(
    (itemId: string): Promise<boolean> => {
      return enqueueMutation(
        (current) => current.filter((item) => item.id !== itemId),
        () => api.deleteItem(companyId, productId, itemId),
      ).then((result) => result !== null);
    },
    [companyId, productId, fetchItems],
  );

  /**
   * Reorder items. Optimistically updates sortOrder values.
   */
  const reorderItems = useCallback(
    (orderedIds: string[]): Promise<boolean> => {
      return enqueueMutation(
        (current) => {
          // Build a map of id → new sortOrder based on position in orderedIds
          const orderMap = new Map(orderedIds.map((id, index) => [id, index]));
          return current
            .map((item) => {
              const newOrder = orderMap.get(item.id);
              if (newOrder !== undefined) {
                return { ...item, sortOrder: newOrder };
              }
              return item;
            })
            .sort((a, b) => a.sortOrder - b.sortOrder);
        },
        () => api.reorderItems(companyId, productId, orderedIds),
      ).then((result) => result !== null);
    },
    [companyId, productId, fetchItems],
  );

  return {
    tools,
    parts,
    isLoading,
    error,
    refetch,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
  };
}
